import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  VpnKey as PasswordIcon,
  PersonOff as InactiveIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import { usersService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const ROLES = [
  { value: 'ADMIN', label: 'Amministratore', color: 'error' },
  { value: 'OPERATOR', label: 'Operatore', color: 'warning' },
  { value: 'VIEWER', label: 'Visualizzatore', color: 'default' },
];

function Users() {
  const { isAdmin, user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'VIEWER',
  });
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    role: 'VIEWER',
    is_active: true,
  });
  const [newPassword, setNewPassword] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadUsers = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const data = await usersService.getAll(includeInactive);
      setUsers(data.users || data);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
      setSnackbar({ open: true, message: 'Errore caricamento utenti', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [includeInactive, isAdmin]);

  // Redirect se non admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleCreateUser = async () => {
    try {
      await usersService.create(formData);
      setSnackbar({ open: true, message: 'Utente creato con successo', severity: 'success' });
      setCreateDialogOpen(false);
      setFormData({ username: '', email: '', password: '', role: 'VIEWER' });
      loadUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore creazione utente';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    try {
      await usersService.update(selectedUser.id, editFormData);
      setSnackbar({ open: true, message: 'Utente aggiornato con successo', severity: 'success' });
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore aggiornamento utente';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await usersService.delete(selectedUser.id);
      setSnackbar({ open: true, message: 'Utente eliminato con successo', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore eliminazione utente';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await usersService.resetPassword(selectedUser.id, newPassword);
      setSnackbar({ open: true, message: 'Password reimpostata con successo', severity: 'success' });
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore reimpostazione password';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      username: user.username,
      email: user.email,
      role: user.role.toUpperCase(),
      is_active: user.is_active,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const getRoleChip = (role) => {
    const roleConfig = ROLES.find((r) => r.value === role.toUpperCase()) || ROLES[2];
    return <Chip label={roleConfig.label} color={roleConfig.color} size="small" />;
  };

  return (
    <Box sx={{ p: 2, pt: 8, height: '100%', overflow: 'auto' }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gestione Utenti
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={loadUsers} size="small">
                <RefreshIcon />
              </IconButton>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setCreateDialogOpen(true)}
              >
                {isMobile ? '' : 'Nuovo Utente'}
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    {!isMobile && <TableCell>Email</TableCell>}
                    <TableCell>Ruolo</TableCell>
                    {!isMobile && <TableCell>Stato</TableCell>}
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {!user.is_active && <InactiveIcon color="disabled" fontSize="small" />}
                          {user.username}
                        </Box>
                      </TableCell>
                      {!isMobile && <TableCell>{user.email}</TableCell>}
                      <TableCell>{getRoleChip(user.role)}</TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Attivo' : 'Disattivato'}
                            color={user.is_active ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Tooltip title="Modifica">
                          <IconButton size="small" onClick={() => openEditDialog(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reimposta Password">
                          <IconButton size="small" onClick={() => openPasswordDialog(user)}>
                            <PasswordIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteDialog(user)}
                            disabled={user.id === currentUser?.id}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : 5} align="center">
                        Nessun utente trovato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crea Utente */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setCreateDialogOpen(false)}
              sx={{ mr: 2 }}
            >
              <CloseIcon />
            </IconButton>
          )}
          Nuovo Utente
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required
              helperText="Minimo 8 caratteri"
            />
            <TextField
              label="Ruolo"
              select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              fullWidth
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!formData.username || !formData.email || !formData.password}
          >
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Modifica Utente */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setEditDialogOpen(false)}
              sx={{ mr: 2 }}
            >
              <CloseIcon />
            </IconButton>
          )}
          Modifica Utente
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Username"
              value={editFormData.username}
              onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Ruolo"
              select
              value={editFormData.role}
              onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
              fullWidth
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Stato"
              select
              value={editFormData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.value === 'active' })}
              fullWidth
            >
              <MenuItem value="active">Attivo</MenuItem>
              <MenuItem value="inactive">Disattivato</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleEditUser}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.username}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Questa azione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Reset Password */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reimposta Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Imposta una nuova password per <strong>{selectedUser?.username}</strong>
          </Typography>
          <TextField
            label="Nuova Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            helperText="Minimo 8 caratteri"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={newPassword.length < 8}
          >
            Reimposta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Users;
